import { useEffect, useState } from 'react';
import type { LinkPreviewDTO } from '@clanfire/shared';
import { embedApi } from '@/api/endpoints';
import { splitLink } from '@/lib/markdown';

/**
 * Up to three distinct web links from a message that deserve a preview card.
 * Links in code, inside spoilers, wrapped in <angle brackets> (the usual
 * "don't embed" convention) or pointing at this app are skipped.
 */
export function extractPreviewUrls(content: string): string[] {
  const visible = content
    .replace(/```[\s\S]*?```|`[^`\n]*`/g, ' ')
    .replace(/\|\|[\s\S]*?\|\|/g, ' ')
    .replace(/<https?:\/\/[^\s>]+>/g, ' ');
  const urls = new Set<string>();
  for (const match of visible.matchAll(/https?:\/\/[^\s<>"]+/g)) {
    // Same trailing-punctuation rule as the markdown renderer.
    const [url] = splitLink(match[0]);
    try {
      if (new URL(url).host === location.host) continue;
    } catch {
      continue;
    }
    urls.add(url);
    if (urls.size === 3) break;
  }
  return [...urls];
}

// Shared between every message linking the same page; failures retry later.
const previews = new Map<string, Promise<LinkPreviewDTO | null>>();
function loadPreview(url: string) {
  let pending = previews.get(url);
  if (!pending) {
    pending = embedApi.preview(url).then((p) => p ?? null);
    previews.set(url, pending);
    pending.catch(() => setTimeout(() => previews.delete(url), 60_000));
  }
  return pending;
}

export function LinkEmbed({ url }: { url: string }) {
  const [preview, setPreview] = useState<LinkPreviewDTO | null>(null);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadPreview(url)
      .then((p) => !cancelled && setPreview(p))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!preview) return null;
  const image = preview.imageUrl && !imageFailed ? preview.imageUrl : null;
  const imageOnly = !preview.title && !preview.description;
  if (imageOnly && !image) return null;

  if (imageOnly) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer nofollow" className="mt-1.5 block w-fit max-w-full">
        <img src={image!} alt="" loading="lazy" onError={() => setImageFailed(true)} className="max-h-[300px] max-w-[min(400px,100%)] rounded-lg border border-line object-contain" />
      </a>
    );
  }

  return (
    <div
      className="mt-1.5 grid max-w-[432px] gap-2 overflow-hidden rounded-lg border border-line border-l-4 bg-sidebar p-3"
      style={{ borderLeftColor: preview.themeColor ?? 'var(--clanfire-border-strong)' }}
    >
      {preview.siteName && <p className="truncate text-xs text-fg-muted">{preview.siteName}</p>}
      {preview.title && (
        <a href={url} target="_blank" rel="noopener noreferrer nofollow" className="line-clamp-2 font-semibold text-link hover:underline">
          {preview.title}
        </a>
      )}
      {preview.description && <p className="line-clamp-3 text-sm text-fg-soft">{preview.description}</p>}
      {image && (
        <a href={url} target="_blank" rel="noopener noreferrer nofollow" tabIndex={-1}>
          <img src={image} alt="" loading="lazy" onError={() => setImageFailed(true)} className="max-h-[220px] w-full rounded-md object-cover" />
        </a>
      )}
    </div>
  );
}
